// Revenue & margin (SCR-011, FLOW-006): one filter bar (months, cost basis, customer, model), four
// stat cards, the stacked "Billed by month" chart beside the Customers / Models / Months breakdown
// (the Months tab is the chart's table), the customers not billed (never mixed into the totals) and
// the current month's forecast. Open months are previews; CUR figures not final are provisional.
import { useContext, useEffect, useMemo, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import type { ScreenProps } from "../../router/route";
import { SessionContext } from "../../session/session";
import { BarChart } from "@nexus/ui";
import { useCustomerDirectory, CustomerPicker } from "../../ui/customer-directory";
import { errorId, Field, LabelText } from "@nexus/ui";
import { Icon } from "@nexus/ui";
import { Link } from "@nexus/ui";
import { formatPercentBp } from "@nexus/ui";
import { MonthRangePicker } from "@nexus/ui";
import { monthLabel } from "@nexus/ui";
import { Card, Chip, FilterBar, NameCell, PageHeader, PillTabs, Segmented, ShareBar, StatCard, StatGrid } from "@nexus/ui";
import { ErrorBanner } from "@nexus/ui";
import { DataTable, Money, type Column } from "@nexus/ui";
import { ForecastPanel } from "./forecast";
import {
  AWS_PAID_BY_LABELS,
  BASIS_OPTIONS,
  BREAKDOWN_PATHS,
  NOT_BILLED_EMPTY_MESSAGE,
  REVENUE_EMPTY_MESSAGE,
  REVENUE_SERIES,
  TAB_COLUMN,
  TABS,
  breakdownRows,
  defaultRevenueFilters,
  forecastQuery,
  monthChartBars,
  monthsLabel,
  provisionalNote,
  rangeError,
  revenueQuery,
  revenueStats,
  selectBreakdown,
  shareLabel,
  validateRevenueFilters,
  type BreakdownData,
  type BreakdownRow,
  type NotBilledOrgItem,
  type RevenueBreakdown,
  type RevenueFilters,
  type RevenueForecast,
  type RevenueSummary,
  type RevenueTab,
} from "./model";

function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  throw error;
}

const EMPTY_BREAKDOWNS: BreakdownData = { byOrg: null, byModel: null, byMonth: null };

export function Revenue(_props: ScreenProps<"/revenue">) {
  const client = useApi();
  const services = useContext(SessionContext);
  const session = services?.state.status === "signed-in" ? services.state.session : null;
  const canOpenCustomers = session?.allows("super-admin") ?? false;
  const directory = useCustomerDirectory();

  const [filters, setFilters] = useState<RevenueFilters>(() => defaultRevenueFilters(new Date()));
  const [tab, setTab] = useState<RevenueTab>("by-org");
  const [modelInput, setModelInput] = useState("");
  const [knownModels, setKnownModels] = useState<readonly string[]>([]);

  const [summary, setSummary] = useState<RevenueSummary | null>(null);
  const [summaryError, setSummaryError] = useState<ApiError | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [breakdowns, setBreakdowns] = useState<BreakdownData>(EMPTY_BREAKDOWNS);
  const [breakdownError, setBreakdownError] = useState<ApiError | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [monthsError, setMonthsError] = useState<ApiError | null>(null);

  const [forecast, setForecast] = useState<RevenueForecast | null>(null);
  const [forecastError, setForecastError] = useState<ApiError | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);

  const fieldErrors = validateRevenueFilters(filters);
  const windowError = rangeError(fieldErrors);
  const valid = windowError === null;
  const query = useMemo(() => revenueQuery(filters), [filters]);
  const queryKey = JSON.stringify(query);

  const store = (which: RevenueTab, result: RevenueBreakdown) => {
    setBreakdowns((current) => ({
      byOrg: which === "by-org" ? result : current.byOrg,
      byModel: which === "by-model" ? result : current.byModel,
      byMonth: which === "by-month" ? result : current.byMonth,
    }));
    if (which === "by-org") directory.remember(result.items.map((item) => ({ id: item.key, name: item.label || item.key })));
    if (which === "by-model" && !filters.model) {
      setKnownModels((known) => [...new Set([...known, ...result.items.map((item) => item.key).filter((key) => key !== "plan_level")])].sort());
    }
  };

  const loadSummary = () => {
    if (!valid) return;
    setSummaryLoading(true);
    setSummaryError(null);
    client
      .get("/v1/admin/revenue/summary", { query })
      .then(setSummary)
      .catch((error: unknown) => setSummaryError(asApiError(error)))
      .finally(() => setSummaryLoading(false));
  };

  // The chart always needs the months, whichever tab is open.
  const loadMonths = () => {
    if (!valid) return;
    setMonthsError(null);
    client
      .get(BREAKDOWN_PATHS["by-month"], { query })
      .then((result) => store("by-month", result))
      .catch((error: unknown) => setMonthsError(asApiError(error)));
  };

  const loadBreakdown = (which: RevenueTab) => {
    if (!valid || which === "by-month") return;
    setBreakdownLoading(true);
    setBreakdownError(null);
    client
      .get(BREAKDOWN_PATHS[which], { query })
      .then((result) => store(which, result))
      .catch((error: unknown) => setBreakdownError(asApiError(error)))
      .finally(() => setBreakdownLoading(false));
  };

  useEffect(() => {
    setBreakdowns(EMPTY_BREAKDOWNS);
    loadSummary();
    loadMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  useEffect(() => {
    loadBreakdown(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, queryKey]);

  const loadForecast = () => {
    setForecastLoading(true);
    setForecastError(null);
    client
      .get("/v1/admin/revenue/forecast", { query: forecastQuery(filters) })
      .then(setForecast)
      .catch((error: unknown) => setForecastError(asApiError(error)))
      .finally(() => setForecastLoading(false));
  };

  useEffect(loadForecast, [filters.orgId]);

  const openMonths = summary?.openMonths ?? [];
  const customerCount = breakdowns.byOrg ? breakdowns.byOrg.items.length : null;
  const stats = summary ? revenueStats(summary, filters.basis, customerCount) : null;
  const note = summary ? provisionalNote(summary) : null;
  const notBilled: readonly NotBilledOrgItem[] = summary?.notBilled ?? [];
  const active = selectBreakdown(tab, breakdowns);
  const rows = active ? breakdownRows(tab, active.items, filters.basis, openMonths) : [];
  const bars = breakdowns.byMonth ? monthChartBars(breakdowns.byMonth.items, filters, filters.basis, openMonths) : [];
  const activeError = tab === "by-month" ? monthsError : breakdownError;
  const activeLoading = tab === "by-month" ? breakdowns.byMonth === null && monthsError === null && valid : breakdownLoading;

  const nameCell = (row: BreakdownRow) => {
    const label = (
      <>
        {tab === "by-org" && canOpenCustomers ? <Link href={`/customers/${encodeURIComponent(row.key)}/usage`}>{row.label}</Link> : row.label}
        {row.open && (
          <>
            {" "}
            <Chip tone="amber">Open</Chip>
          </>
        )}
        {row.provisional && (
          <>
            {" "}
            <Chip tone="amber" title="A CUR figure in it is not final yet">
              Provisional
            </Chip>
          </>
        )}
      </>
    );
    return label;
  };

  const allColumns: Column<BreakdownRow>[] = [
    { key: "label", header: TAB_COLUMN[tab].header, cell: nameCell, compare: (a, b) => a.key.localeCompare(b.key) },
    { key: "billed", header: "Billed", numeric: true, help: "common.billed", cell: (row) => <Money micro={row.billed} />, compare: (a, b) => cmp(a.billed, b.billed) },
    {
      key: "cost",
      header: "AWS cost",
      numeric: true,
      help: "common.awsCost",
      cell: (row) => (
        <span class="cell-cost">
          <Money micro={row.awsCost} />
        </span>
      ),
      compare: (a, b) => cmp(a.awsCost, b.awsCost),
    },
    {
      key: "profit",
      header: "Profit",
      numeric: true,
      cell: (row) => (
        <span class="cell-profit">
          <Money micro={row.profit} />
        </span>
      ),
      compare: (a, b) => cmp(a.profit, b.profit),
    },
    {
      key: "share",
      header: "Share",
      help: "finance.share",
      cell: (row) => (
        <span class="share-cell">
          <ShareBar fraction={row.share} label={`${shareLabel(row.share)} of billed`} />
          <span class="share-text" aria-hidden="true">
            {shareLabel(row.share)}
          </span>
        </span>
      ),
      compare: (a, b) => a.share - b.share,
    },
  ];

  // The Months tab is the chart's table, so it carries the chart's AWS cost too.
  const columns = tab === "by-month" ? allColumns : allColumns.filter((column) => column.key !== "cost");

  const notBilledColumns: Column<NotBilledOrgItem>[] = [
    {
      key: "label",
      header: "Customer",
      cell: (row) => (canOpenCustomers ? <NameCell name={row.label || row.orgId} id={row.orgId} href={`/customers/${encodeURIComponent(row.orgId)}/billing`} /> : row.label || row.orgId),
      compare: (a, b) => a.label.localeCompare(b.label),
    },
    { key: "months", header: "Months", cell: (row) => monthsLabel(row.months) },
    { key: "awsPaidBy", header: "AWS paid by", help: "finance.awsPaidBy", cell: (row) => AWS_PAID_BY_LABELS[row.awsPaidBy] },
    {
      key: "listCost",
      header: "AWS cost at list price",
      numeric: true,
      cell: (row) => (
        <span class="cell-cost">
          <Money micro={row.listCostMicroUsd} />
        </span>
      ),
      compare: (a, b) => a.listCostMicroUsd - b.listCostMicroUsd,
    },
    {
      key: "expense",
      header: "AllCode's AWS cost",
      numeric: true,
      help: "finance.allcodeCost",
      cell: (row) => (row.expenseMicroUsd === null ? "—" : <Money micro={row.expenseMicroUsd} />),
    },
    {
      key: "provisional",
      header: "Status",
      cell: (row) => (row.provisional ? <Chip tone="amber">Provisional</Chip> : <Chip tone="grey">Not billed</Chip>),
    },
  ];

  const set = (patch: Partial<RevenueFilters>) => setFilters((current) => ({ ...current, ...patch }));

  return (
    <div class="page revenue-screen">
      <PageHeader
        title="Revenue & margin"
        lede="What customers were billed, what AWS charged for their usage, and the profit in between."
        actions={
          <a class="button" href={client.csvUrl("/v1/admin/revenue/summary", { query })} download>
            <Icon name="download" />
            Export CSV
          </a>
        }
      />

      <FilterBar>
        <div class="filter">
          <LabelText id="revenue-months-label" label="Months" help="common.months" compact />
          <MonthRangePicker
            id="revenue-months"
            labelledBy="revenue-months-label"
            value={{ from: filters.from, to: filters.to }}
            onChange={(range) => set({ from: range.from, to: range.to })}
            describedBy={windowError ? errorId("revenue-months") : undefined}
          />
          {windowError && (
            <p class="field-error" id={errorId("revenue-months")} role="alert">
              {windowError}
            </p>
          )}
        </div>
        <div class="filter">
          <LabelText id="revenue-basis-label" label="Cost basis" help="common.costBasis" compact />
          <Segmented options={BASIS_OPTIONS} value={filters.basis} onChange={(basis) => set({ basis })} labelledBy="revenue-basis-label" />
        </div>
        <Field id="revenue-customer" label="Customer" help="common.customer" compact class="field-medium">
          <CustomerPicker id="revenue-customer" value={filters.orgId ?? ""} allowAll onChange={(orgId) => set({ orgId: orgId || null })} />
        </Field>
        <Field id="revenue-model" label="Model" help="common.model" compact class="field-medium">
          <form
            class="model-filter"
            onSubmit={(event) => {
              event.preventDefault();
              set({ model: modelInput.trim() || null });
            }}
          >
            <input
              id="revenue-model"
              class="input"
              type="text"
              list="revenue-model-options"
              placeholder="All models"
              autocomplete="off"
              spellcheck={false}
              value={modelInput}
              onInput={(event) => setModelInput(event.currentTarget.value)}
              onChange={(event) => set({ model: event.currentTarget.value.trim() || null })}
            />
            <datalist id="revenue-model-options">
              {knownModels.map((model) => (
                <option key={model} value={model} />
              ))}
            </datalist>
          </form>
        </Field>
      </FilterBar>

      {summaryError && <ErrorBanner error={summaryError} onRetry={loadSummary} />}
      {note && (
        <div class="banner banner-info revenue-preview" role="status">
          <p>{note}</p>
          {openMonths.length > 0 && (
            <span class="row">
              {openMonths.map((openMonth) => (
                <Chip key={openMonth} tone="amber">
                  {monthLabel(openMonth)} open
                </Chip>
              ))}
            </span>
          )}
        </div>
      )}

      {stats && (
        <StatGrid label="Totals for these months">
          <StatCard label="Billed to customers" help="common.billed" value={<Money micro={stats.billed} />} note={stats.billedNote} />
          <StatCard label="AWS cost" help="common.awsCost" value={<Money micro={stats.awsCost} />} note={stats.awsCostNote} />
          <StatCard label="Gross profit" help="common.grossProfit" accent value={<Money micro={stats.grossProfit} />} note={stats.grossProfitNote} />
          <StatCard
            label="Markup and margin"
            help="common.markupAndMargin"
            value={<span title="Markup: profit ÷ AWS cost">{formatPercentBp(stats.markupBp)}</span>}
            secondary={<span title="Margin: profit ÷ billed">{formatPercentBp(stats.marginBp)}</span>}
            note="Markup on cost · margin on billed"
          />
        </StatGrid>
      )}

      <div class="revenue-split">
        <Card title="Billed by month">
          {monthsError ? (
            <ErrorBanner error={monthsError} onRetry={loadMonths} />
          ) : (
            bars.length > 0 && (
              <BarChart
                bars={bars}
                series={REVENUE_SERIES}
                label={`Billed by month, ${monthLabel(filters.from)} to ${monthLabel(filters.to)}: AWS cost and profit stacked`}
                caption={`Hover a bar for exact figures. The same numbers are in the Months tab of the breakdown.${openMonths.length > 0 ? " Lighter bars are open months." : ""}`}
              />
            )
          )}
        </Card>

        <Card title="Breakdown" help="finance.breakdown" actions={<PillTabs tabs={TABS} value={tab} onChange={(value: RevenueTab) => setTab(value)} label="Break the totals down by" controls="revenue-breakdown" />}>
          <div id="revenue-breakdown" role="tabpanel" aria-label={TAB_COLUMN[tab].caption}>
            <DataTable
              caption={TAB_COLUMN[tab].caption}
              hideCaption
              columns={columns}
              rows={rows}
              rowKey={(row) => row.key}
              loading={activeLoading}
              error={activeError}
              onRetry={() => (tab === "by-month" ? loadMonths() : loadBreakdown(tab))}
              emptyMessage={REVENUE_EMPTY_MESSAGE}
              csvHref={valid ? client.csvUrl(BREAKDOWN_PATHS[tab], { query }) : undefined}
            />
          </div>
        </Card>
      </div>

      <Card title="Customers not billed" help="finance.notBilled">
        <p class="card-note">Their usage is not in the totals above. When AllCode pays their AWS bill, that cost comes off the gross profit.</p>
        <DataTable
          caption="Customers not billed in these months"
          hideCaption
          columns={notBilledColumns}
          rows={notBilled}
          rowKey={(row) => row.orgId}
          loading={summaryLoading}
          emptyMessage={NOT_BILLED_EMPTY_MESSAGE}
        />
      </Card>

      <ForecastPanel forecast={forecast} loading={forecastLoading} error={forecastError} onRetry={loadForecast} canOpenCustomers={canOpenCustomers} />
    </div>
  );
}

function cmp(a: bigint, b: bigint): number {
  return a === b ? 0 : a < b ? -1 : 1;
}
